/* The Optimizer module: blocks of technician changes for the desk to approve, grouped
 * by day. Option A in the design, the checklist.
 *
 * It waits to be looked at. Nothing here opens by itself or makes a sound, and every
 * outcome is a sentence on the page. Offered only to a salon whose licence includes
 * `optimizer` and that has it switched on, and the function refuses anyone else. The data is
 * js/optimizer.js, the rules and wording are js/rules/optimizer-view.js.
 */
import * as Vue from 'vue';
import { apps } from '../apps.js';
import { optimizer } from '../optimizer.js';
import {
  approveState, blockTitle, canTick, checkAge, gainLine, lineNotes, lineText, resultText, settledIds, tickedIds
} from '../rules/optimizer-view.js';

var OptimizerBlock = {
  name: 'OptimizerBlock',
  props: { block: { type: Object, required: true } },
  setup: function (props) {
    var state = optimizer.state;
    var ticks = Vue.computed(function () { return optimizer.ticksFor(props.block); });
    var ticked = Vue.computed(function () { return tickedIds(props.block, ticks.value, state.results); });
    var busy = Vue.computed(function () { return !!state.busy[props.block.key]; });

    var lines = Vue.computed(function () {
      return props.block.lines.map(function (line) {
        return {
          line: line,
          text: lineText(line),
          notes: lineNotes(line),
          result: resultText(state.results[line.id]),
          tickable: canTick(line, props.block, state.results),
          on: ticked.value.indexOf(line.id) !== -1
        };
      });
    });

    return {
      state: state,
      title: Vue.computed(function () { return blockTitle(props.block); }),
      // What is ticked now plus what has already moved, because that time is open too.
      gain: Vue.computed(function () {
        return gainLine(props.block, ticked.value.concat(settledIds(props.block, state.results)));
      }),
      approval: Vue.computed(function () { return approveState(props.block, ticked.value, { busy: busy.value }); }),
      lines: lines,
      busy: busy,
      message: Vue.computed(function () { return state.messages[props.block.key] || null; }),
      setTick: function (line, on) { optimizer.setTick(props.block, line, on); },
      approve: function () { optimizer.approve(props.block, ticked.value); },
      dismissBlock: function () { optimizer.dismiss(props.block.date, props.block); },
      dismissLine: function (line) { optimizer.dismiss(props.block.date, line); }
    };
  },
  template: [
    '<article class="opt-block">',
    '  <h4 class="opt-block__title">{{ title }}</h4>',
    '  <p class="opt-gain">{{ gain }}</p>',
    '  <ul class="opt-lines">',
    '    <li v-for="l in lines" :key="l.line.id" class="opt-line"',
    '      :class="{ \'is-stay\': l.line.kind !== \'move\', \'is-done\': l.result && l.result.kind === \'ok\' }">',
    '      <label class="opt-line__main">',
    '        <input type="checkbox" class="opt-line__box" :checked="l.on" :disabled="!l.tickable || busy"',
    '          @change="setTick(l.line, $event.target.checked)" />',
    '        <span class="opt-line__time">{{ l.text.time }}</span>',
    '        <span class="opt-line__what">',
    '          <strong>{{ l.text.service }}</strong><span v-if="l.text.client"> · {{ l.text.client }}</span>',
    '          <span v-if="l.line.kind === \'move\'" class="opt-line__who">{{ l.text.from }} → <strong>{{ l.text.to }}</strong></span>',
    '          <span v-for="(n, i) in l.notes" :key="i" class="opt-note" :class="\'opt-note--\' + n.kind">',
    '            <template v-if="n.kind === \'warn\'">⚠ </template>{{ n.text }}</span>',
    '          <span v-if="l.result" class="opt-result" :class="\'opt-result--\' + l.result.kind">{{ l.result.text }}</span>',
    '        </span>',
    '      </label>',
    '      <button v-if="l.line.kind === \'move\' && !(l.result && l.result.kind === \'ok\')" type="button"',
    '        class="opt-line__dismiss" :disabled="busy" @click="dismissLine(l.line)">Dismiss</button>',
    '    </li>',
    '  </ul>',
    '  <p v-if="approval.reason" class="opt-muted">{{ approval.reason }}</p>',
    '  <p v-if="message" class="opt-message">{{ message }}</p>',
    '  <div class="opt-actions">',
    '    <button type="button" class="btn btn--primary btn--sm" :disabled="approval.disabled" @click="approve">',
    '      {{ busy ? "Saving…" : approval.label }}</button>',
    '    <button type="button" class="btn btn--sm" :disabled="busy" @click="dismissBlock">Dismiss</button>',
    '  </div>',
    '</article>'
  ].join('\n')
};

var AGE_TICK_MS = 30000;

var OptimizerPage = {
  name: 'OptimizerPage',
  components: { OptimizerBlock: OptimizerBlock },
  setup: function () {
    var state = optimizer.state;
    var now = Vue.ref(Date.now());
    var timer = null;

    Vue.onMounted(function () {
      optimizer.load();
      timer = setInterval(function () { now.value = Date.now(); }, AGE_TICK_MS);
    });
    Vue.onUnmounted(function () { clearInterval(timer); });

    return {
      state: state,
      enabled: optimizer.enabled,
      dryRun: optimizer.dryRun,
      groups: optimizer.groups,
      age: Vue.computed(function () {
        return checkAge(state.settings && state.settings.last_check_ok_at, now.value);
      }),
      checkNow: optimizer.checkNow,
      isOpen: function (day) {
        return state.open[day.date] == null ? day.expanded : state.open[day.date];
      },
      toggle: function (day, open) { state.open[day.date] = !open; }
    };
  },
  template: [
    '<div class="opt">',
    '  <header class="opt-head">',
    '    <h2 class="opt-title">Optimizer</h2>',
    '    <template v-if="enabled">',
    '      <span class="opt-muted">{{ age }}</span>',
    '      <button type="button" class="btn btn--sm opt-head__check" :disabled="state.checking" @click="checkNow">',
    '        {{ state.checking ? "Checking…" : "Check now" }}</button>',
    '    </template>',
    '  </header>',
    '  <p v-if="state.checkMessage" class="opt-message">{{ state.checkMessage }}</p>',
    '',
    /* Reached only while switched on (see `available`), and it can go off while open. */
    '  <div v-if="!enabled" class="opt-notice" role="status">The Optimizer is switched off for this salon.</div>',
    '',
    '  <template v-else>',
    '    <div v-if="dryRun" class="opt-banner" role="status"><strong>Dry run.</strong>',
    '      Approving goes through every check and saves nothing to Mangomint.</div>',
    '    <p v-if="!groups.length" class="opt-muted">Nothing to review.</p>',
    '    <section v-for="day in groups" :key="day.date" class="opt-day">',
    '      <button type="button" class="opt-day__head" :aria-expanded="isOpen(day) ? \'true\' : \'false\'"',
    '        @click="toggle(day, isOpen(day))">',
    '        <span class="opt-day__label">{{ day.label }}</span>',
    '        <span class="opt-muted">',
    '          <template v-if="day.label === \'Today\'">{{ day.dateLabel }} · </template>{{ day.countLabel }}</span>',
    '        <span class="opt-day__caret" aria-hidden="true">{{ isOpen(day) ? "▾" : "›" }}</span>',
    '      </button>',
    '      <template v-if="isOpen(day)">',
    '        <p v-if="day.highlight" class="opt-highlight">⚑ {{ day.highlight }}</p>',
    '        <optimizer-block v-for="b in day.blocks" :key="b.key" :block="b" />',
    '      </template>',
    '    </section>',
    '  </template>',
    '</div>'
  ].join('\n')
};

apps.register({
  id: 'optimizer',
  name: 'Optimizer',
  icon: '⇄',
  blurb: 'Technician changes that open up online booking for scarce services.',
  page: OptimizerPage,
  badge: optimizer.count,
  // The board stays where a desk that has never touched the switcher comes in.
  landing: false,
  // Licensed is not enough: a salon with it switched off sees no entry at all.
  available: optimizer.offered
});

export { OptimizerPage };
